/**
 * Engine client — S2-T2.5.
 *
 * `stampDocument` runs the engine for the preview. In a browser it dispatches
 * to the Web Worker (engine.worker.ts) so stamping never blocks the main
 * thread; where no Worker exists (tests, SSR) it falls back to running the
 * engine inline. Callers (useStampedPreview) don't know or care which path
 * ran — the signature is identical.
 */

import { stampBatch } from "../engine/bates-engine";
import type { StampingConfig } from "../engine/models";
import type { LoadedPdf } from "./components/DropZone";

interface PendingRequest {
  resolve: (bytes: Uint8Array | null) => void;
  reject: (error: Error) => void;
}

interface StampResponse {
  id: number;
  ok: boolean;
  bytes?: Uint8Array | null;
  error?: string;
}

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, PendingRequest>();

function getWorker(): Worker | null {
  if (typeof Worker === "undefined") return null;
  if (!worker) {
    worker = new Worker(new URL("./engine.worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (event: MessageEvent<StampResponse>) => {
      const { id, ok, bytes, error } = event.data;
      const request = pending.get(id);
      if (!request) return;
      pending.delete(id);
      if (ok) request.resolve(bytes ?? null);
      else request.reject(new Error(error ?? "Stamping failed"));
    };
    worker.onerror = () => {
      for (const request of pending.values()) request.reject(new Error("Engine worker crashed"));
      pending.clear();
    };
  }
  return worker;
}

export async function stampDocument(
  loaded: LoadedPdf,
  config: StampingConfig,
): Promise<Uint8Array | null> {
  const w = getWorker();

  if (!w) {
    const { outputs } = await stampBatch(
      [{ fileName: loaded.name, bytes: loaded.bytes.slice() }],
      config,
      config.startNumber,
    );
    return outputs[0]?.bytes ?? null;
  }

  const id = nextId++;
  return new Promise<Uint8Array | null>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    w.postMessage({
      id,
      fileName: loaded.name,
      bytes: loaded.bytes.slice(),
      config,
      startNumber: config.startNumber,
    });
  });
}
